import { useState, useEffect } from 'react';
import axios from 'axios';
import PhoneIcon from '@mui/icons-material/Phone';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';

// Contact/social/app bar. Phone/hours/social links come from the admin's
// Site Content page. App download links + status text come from the
// admin's Header settings (Admin > Header), the same source the header's
// "Download App" page reads from, so both stay in sync automatically.
const FooterInfoBar = () => {
    const [footerPhone, setFooterPhone] = useState("");
    const [footerHours, setFooterHours] = useState("");
    const [social, setSocial] = useState({ facebook: "", twitter: "", instagram: "" });
    const [androidAppUrl, setAndroidAppUrl] = useState("");
    const [iosAppUrl, setIosAppUrl] = useState("");
    const [appDownloadStatusText, setAppDownloadStatusText] = useState("Coming soon");

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await axios.get('/api/v1/site-settings');
                setFooterPhone(data.settings.footerPhone || "");
                setFooterHours(data.settings.footerHours || "");
                setSocial({
                    facebook: data.settings.footerSocial?.facebook || "",
                    twitter: data.settings.footerSocial?.twitter || "",
                    instagram: data.settings.footerSocial?.instagram || "",
                });
            } catch (error) {
                console.error('Failed to load footer settings', error);
            }
        };
        fetchSettings();

        const fetchHeaderSettings = async () => {
            try {
                const { data } = await axios.get('/api/v1/header-settings');
                setAndroidAppUrl(data.settings.androidAppUrl || "");
                setIosAppUrl(data.settings.iosAppUrl || "");
                setAppDownloadStatusText(data.settings.appDownloadStatusText || "Coming soon");
            } catch (error) {
                console.error('Failed to load app download settings', error);
            }
        };
        fetchHeaderSettings();
    }, []);

    const socialIconClass = (url) =>
        `w-9 h-9 rounded-full border flex items-center justify-center ${url ? "text-gray-600 hover:text-primary-blue hover:border-primary-blue" : "text-gray-400 cursor-not-allowed"}`;

    return (
        <div className="w-full mt-8 px-4 sm:px-12 py-4 sm:py-6 bg-white flex flex-col sm:flex-row justify-between items-center gap-6">

            <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full border flex items-center justify-center text-gray-500 flex-shrink-0">
                    <PhoneIcon sx={{ fontSize: "20px" }} />
                </span>
                <div>
                    <p className="font-bold text-gray-800 text-base leading-tight">{footerPhone || "Add Your Phone Number"}</p>
                    <p className="text-xs text-gray-400">{footerHours || "Add Store Hours"}</p>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                <div className="text-center sm:text-right">
                    <p className="font-bold text-gray-800 text-sm leading-tight">Download App on Mobile:</p>
                    {!androidAppUrl && !iosAppUrl && (
                        <p className="text-xs text-gray-400">{appDownloadStatusText}</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {androidAppUrl ? (
                        <a href={androidAppUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-gray-900 text-white text-xs rounded-md px-3 py-2 hover:opacity-90">
                            <AndroidIcon sx={{ fontSize: "18px" }} />
                            <span className="leading-tight">Google Play</span>
                        </a>
                    ) : (
                        <span className="flex items-center gap-1.5 bg-gray-900 text-white text-xs rounded-md px-3 py-2 cursor-not-allowed opacity-60">
                            <AndroidIcon sx={{ fontSize: "18px" }} />
                            <span className="leading-tight">Google Play</span>
                        </span>
                    )}
                    {iosAppUrl ? (
                        <a href={iosAppUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-gray-900 text-white text-xs rounded-md px-3 py-2 hover:opacity-90">
                            <AppleIcon sx={{ fontSize: "18px" }} />
                            <span className="leading-tight">App Store</span>
                        </a>
                    ) : (
                        <span className="flex items-center gap-1.5 bg-gray-900 text-white text-xs rounded-md px-3 py-2 cursor-not-allowed opacity-60">
                            <AppleIcon sx={{ fontSize: "18px" }} />
                            <span className="leading-tight">App Store</span>
                        </span>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-3">
                {social.facebook ? (
                    <a href={social.facebook} target="_blank" rel="noopener noreferrer" className={socialIconClass(social.facebook)}>
                        <FacebookIcon sx={{ fontSize: "18px" }} />
                    </a>
                ) : (
                    <span className={socialIconClass(null)}>
                        <FacebookIcon sx={{ fontSize: "18px" }} />
                    </span>
                )}
                {social.twitter ? (
                    <a href={social.twitter} target="_blank" rel="noopener noreferrer" className={socialIconClass(social.twitter)}>
                        <TwitterIcon sx={{ fontSize: "18px" }} />
                    </a>
                ) : (
                    <span className={socialIconClass(null)}>
                        <TwitterIcon sx={{ fontSize: "18px" }} />
                    </span>
                )}
                {social.instagram ? (
                    <a href={social.instagram} target="_blank" rel="noopener noreferrer" className={socialIconClass(social.instagram)}>
                        <InstagramIcon sx={{ fontSize: "18px" }} />
                    </a>
                ) : (
                    <span className={socialIconClass(null)}>
                        <InstagramIcon sx={{ fontSize: "18px" }} />
                    </span>
                )}
            </div>

        </div>
    );
};

export default FooterInfoBar;
